// src/pages/AdminDestinationsPage.js — pickup/dropoff locations admin.
// Backed by /api/destinations — see backend/routes/destinations.js.

import React, { useCallback, useEffect, useState } from "react";
import { api } from "../scripts/api";
import Modal from "../components/Modal";
import { MapPin, Plus, Edit2, Trash2, Archive, RefreshCw, Search } from "lucide-react";

const DEST_TYPES = [
  { v: 'pickup',   label: 'Pickup'   },
  { v: 'dropoff',  label: 'Dropoff'  },
  { v: 'both',     label: 'Both'     },
  { v: 'factory',  label: 'Factory'  },
  { v: 'customer', label: 'Customer' },
  { v: 'sna',      label: 'SNA'      },
];

export default function AdminDestinationsPage() {
  const [rows, setRows]         = useState([]);
  const [busy, setBusy]         = useState(false);
  const [msg, setMsg]           = useState("");
  const [search, setSearch]     = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [includeInactive, setIncludeInactive] = useState(false);
  const [editing, setEditing]   = useState(null);   // null or row
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setBusy(true); setMsg("");
    try {
      const params = {};
      if (typeFilter) params.type = typeFilter;
      if (!includeInactive) params.active = '1';
      const { data } = await api.get("/destinations", { params });
      setRows(Array.isArray(data?.destinations) ? data.destinations : []);
    } catch (e) {
      setMsg(e?.response?.data?.error || "Failed to load destinations.");
      setRows([]);
    } finally { setBusy(false); }
  }, [typeFilter, includeInactive]);

  useEffect(() => { load(); }, [load]);

  const filtered = rows.filter(r => {
    if (!search.trim()) return true;
    const needle = search.toLowerCase();
    return [r.name, r.address, r.city, r.contact_name, r.notes]
      .some(v => String(v || '').toLowerCase().includes(needle));
  });

  const deactivate = async (r) => {
    if (!window.confirm(`Deactivate "${r.name}"? It will stop appearing in the move destination dropdown but historical moves still reference it.`)) return;
    try { await api.delete(`/destinations/${r.id}`); load(); }
    catch (e) { setMsg(e?.response?.data?.error || "Deactivate failed."); }
  };

  const hardDelete = async (r) => {
    if (!window.confirm(`PERMANENTLY delete "${r.name}"? Historical moves that reference it will lose the link.`)) return;
    try { await api.delete(`/destinations/${r.id}?hard=1`); load(); }
    catch (e) { setMsg(e?.response?.data?.error || "Delete failed."); }
  };

  return (
    <div className="container">
      <div className="row" style={{ justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
        <div>
          <h2 style={{ margin: 0, fontWeight: 900 }}>
            <MapPin size={22} style={{ verticalAlign: -4, marginRight: 8 }} />
            Destinations
          </h2>
          <div className="muted" style={{ marginTop: 4 }}>
            Named pickup/dropoff locations with full addresses. Used by the Drive-Away move form
            and by Distance Matrix for accurate mileage.
          </div>
        </div>
        <div className="row" style={{ gap: 8 }}>
          <button className="btn-outline" onClick={load} disabled={busy}>
            <RefreshCw size={14} style={{ marginRight: 6, verticalAlign: -2 }} />
            Refresh
          </button>
          <button className="btn" onClick={() => setCreating(true)}>
            <Plus size={14} style={{ marginRight: 6, verticalAlign: -2 }} />
            New Destination
          </button>
        </div>
      </div>

      {/* Filter bar */}
      <div style={{
        marginTop: 12, padding: 10, background: "var(--card-bg)",
        border: "1px solid var(--card-border)", borderRadius: 12,
        display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center",
      }}>
        <div style={{ position: "relative", flex: "1 1 220px", maxWidth: 360 }}>
          <Search size={14} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", opacity: 0.5 }} />
          <input className="input" placeholder="Name, city, contact…"
            value={search} onChange={e => setSearch(e.target.value)}
            style={{ width: "100%", paddingLeft: 30 }}
          />
        </div>
        <select className="input" value={typeFilter} onChange={e => setTypeFilter(e.target.value)} style={{ flex: "1 1 140px", maxWidth: 180 }}>
          <option value="">All types</option>
          {DEST_TYPES.map(t => <option key={t.v} value={t.v}>{t.label}</option>)}
        </select>
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13 }}>
          <input type="checkbox" checked={includeInactive} onChange={e => setIncludeInactive(e.target.checked)} />
          Show inactive
        </label>
      </div>

      {msg && <div className="card" style={{ marginTop: 10, color: "#dc2626" }}>{msg}</div>}

      <div className="card" style={{ marginTop: 12, padding: 0, overflow: "hidden" }}>
        {busy && <div className="muted" style={{ padding: 14 }}>Loading…</div>}
        {!busy && filtered.length === 0 && (
          <div className="muted" style={{ padding: 14 }}>No destinations match.</div>
        )}
        {!busy && filtered.length > 0 && (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ background: "rgba(127,127,127,0.06)" }}>
                  <Th>Name</Th><Th>Type</Th><Th>Full Address</Th><Th>Contact</Th><Th>Status</Th><Th>Actions</Th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(d => (
                  <tr key={d.id} style={{ borderTop: "1px solid var(--card-border)", opacity: d.is_active ? 1 : 0.5 }}>
                    <Td>
                      <div style={{ fontWeight: 800 }}>{d.name}</div>
                      {d.notes && <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>{d.notes}</div>}
                    </Td>
                    <Td>
                      <span style={{ padding: "2px 8px", borderRadius: 99, background: "#ede9fe", color: "#6b21a8", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4 }}>
                        {d.type}
                      </span>
                    </Td>
                    <Td style={{ maxWidth: 320 }}>
                      {d.full_address || <span className="muted">—</span>}
                    </Td>
                    <Td>
                      {d.contact_name && <div>{d.contact_name}</div>}
                      {d.contact_phone && <div className="muted" style={{ fontSize: 11 }}>📱 {d.contact_phone}</div>}
                      {d.contact_email && <div className="muted" style={{ fontSize: 11 }}>✉️ {d.contact_email}</div>}
                    </Td>
                    <Td>
                      {d.is_active
                        ? <span style={{ padding: "2px 8px", borderRadius: 99, background: "#dcfce7", color: "#166534", fontSize: 10, fontWeight: 700 }}>ACTIVE</span>
                        : <span style={{ padding: "2px 8px", borderRadius: 99, background: "#f3f4f6", color: "#6b7280", fontSize: 10, fontWeight: 700 }}>INACTIVE</span>}
                    </Td>
                    <Td>
                      <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                        <button className="btn-outline" onClick={() => setEditing(d)} style={{ padding: "4px 8px", fontSize: 11 }}>
                          <Edit2 size={11} style={{ marginRight: 3, verticalAlign: -1 }} />
                          Edit
                        </button>
                        {d.is_active ? (
                          <button className="btn-outline" onClick={() => deactivate(d)} style={{ padding: "4px 8px", fontSize: 11, color: "#92400e" }}>
                            <Archive size={11} style={{ marginRight: 3, verticalAlign: -1 }} />
                            Deactivate
                          </button>
                        ) : (
                          <button className="btn-outline" onClick={() => hardDelete(d)} style={{ padding: "4px 8px", fontSize: 11, color: "#dc2626" }}>
                            <Trash2 size={11} style={{ marginRight: 3, verticalAlign: -1 }} />
                            Delete
                          </button>
                        )}
                      </div>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {(editing || creating) && (
        <DestinationEditor
          destination={editing}
          onClose={() => { setEditing(null); setCreating(false); }}
          onSaved={() => { setEditing(null); setCreating(false); load(); }}
        />
      )}
    </div>
  );
}

const Th = ({ children }) => (
  <th style={{ padding: "10px 12px", textAlign: "left", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4, opacity: 0.6 }}>
    {children}
  </th>
);
const Td = ({ children, style }) => (
  <td style={{ padding: "10px 12px", ...style }}>{children}</td>
);

function DestinationEditor({ destination, onClose, onSaved }) {
  const isNew = !destination?.id;
  const [form, setForm] = useState({
    name:          destination?.name          || "",
    type:          destination?.type          || "pickup",
    address:       destination?.address       || "",
    city:          destination?.city          || "",
    province:      destination?.province      || "",
    postal:        destination?.postal        || "",
    country:       destination?.country       || "Canada",
    contact_name:  destination?.contact_name  || "",
    contact_phone: destination?.contact_phone || "",
    contact_email: destination?.contact_email || "",
    notes:         destination?.notes         || "",
    is_active:     destination?.is_active === 0 ? false : true,
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr]   = useState("");

  const update = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setErr(""); setBusy(true);
    try {
      if (!form.name.trim()) throw new Error("Name is required");
      if (isNew) await api.post("/destinations", form);
      else       await api.patch(`/destinations/${destination.id}`, form);
      onSaved();
    } catch (e) {
      setErr(e?.response?.data?.error || e.message || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <Modal onClose={onClose}>
      <h3 style={{ margin: 0 }}>{isNew ? "New Destination" : `Edit: ${destination.name}`}</h3>
      <div className="muted" style={{ fontSize: 12, marginTop: 2, marginBottom: 14 }}>
        Full address is used by Google Distance Matrix for mileage calculations.
      </div>

      <div style={{ display: "grid", gap: 10 }}>
        <Row>
          <Field label="Name *"><input className="input" value={form.name} onChange={update("name")} autoFocus /></Field>
          <Field label="Type">
            <select className="input" value={form.type} onChange={update("type")}>
              {DEST_TYPES.map(t => <option key={t.v} value={t.v}>{t.label}</option>)}
            </select>
          </Field>
        </Row>
        <Field label="Address (street)"><input className="input" value={form.address} onChange={update("address")} placeholder="e.g. 1234 Crowchild Trail NW" /></Field>
        <Row>
          <Field label="City"><input className="input" value={form.city} onChange={update("city")} /></Field>
          <Field label="Province"><input className="input" value={form.province} onChange={update("province")} placeholder="AB" /></Field>
          <Field label="Postal"><input className="input" value={form.postal} onChange={update("postal")} placeholder="T2L 1Y3" /></Field>
        </Row>
        <Field label="Country"><input className="input" value={form.country} onChange={update("country")} /></Field>

        <div style={{ height: 1, background: "var(--card-border)", margin: "6px 0" }} />
        <Row>
          <Field label="Contact name"><input className="input" value={form.contact_name} onChange={update("contact_name")} /></Field>
          <Field label="Contact phone"><input className="input" value={form.contact_phone} onChange={update("contact_phone")} /></Field>
        </Row>
        <Field label="Contact email"><input className="input" value={form.contact_email} onChange={update("contact_email")} /></Field>

        <Field label="Notes">
          <textarea className="input" style={{ minHeight: 60 }} value={form.notes} onChange={update("notes")} placeholder="Gate codes, receiving hours, site-specific instructions" />
        </Field>

        <label style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 700 }}>
          <input type="checkbox" checked={form.is_active} onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))} />
          Active (visible in move destination dropdown)
        </label>
      </div>

      {err && <div className="card" style={{ marginTop: 10, color: "#dc2626" }}>{err}</div>}

      <div className="row" style={{ justifyContent: "flex-end", gap: 8, marginTop: 14 }}>
        <button className="btn-outline" onClick={onClose} disabled={busy}>Cancel</button>
        <button className="btn" onClick={save} disabled={busy}>{isNew ? "Create" : "Save"}</button>
      </div>
    </Modal>
  );
}

const Row = ({ children }) => <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>{children.map((c, i) => <div key={i} style={{ flex: "1 1 150px", minWidth: 120 }}>{c}</div>)}</div>;
const Field = ({ label, children }) => (
  <div>
    <div className="muted" style={{ fontSize: 12, marginBottom: 4 }}>{label}</div>
    {children}
  </div>
);
